import React, {useState, useEffect} from "react";
import {CardHeader, StyledOrderCard} from "./OrderCard.style";
import {LuClock, LuMinus, LuPlus, LuPrinter} from "react-icons/lu";
import clockIcon from "../../../../assets/clockIcon.svg";
import personIcon from "../../../../assets/LiveOrders/personIcon.svg";
import phoneIcon from "../../../../assets/LiveOrders/phoneIcon.svg";
import locationIcon from "../../../../assets/LiveOrders/locationIcon.svg";
import itemsIcon from "../../../../assets/LiveOrders/itemsIcon.svg";
import tickIcon from "../../../../assets/LiveOrders/tickIcon.svg";
import crossIcon from "../../../../assets/LiveOrders/crossIcon.svg";
import readyIcon from "../../../../assets/LiveOrders/readyIcon.svg";
import tickMark from "../../../../assets/LiveOrders/tickMark.svg";
import eyeIcon from "../../../../assets/LiveOrders/eyeIcon.svg";
import copyIcon from "../../../../assets/LiveOrders/copyIcon.svg";
import Button from "components/molecules/Button";
import OrderDetails from "../OrderDetails";
import orderService from "services/orderService";
import useTimeAgo from "hooks/useTimeAgo";
import Toast from "components/molecules/Toast";
import thermalPrinterService from "services/thermalPrinterService";
import {formatApiOrder} from "../../../../helpers/useCountDown";

const OrderCard = ({orderId, data, onStatusChange, rawOrderData, storeName, index}) => {
    const [detailsSidebar, setDetailsSidebar] = useState(false);
    const timeAgo = useTimeAgo(data?.createdAt);
    const [prepTime, setPrepTime] = useState(20); // Default 20 mins
    const [isPrinting, setIsPrinting] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    // Order expiration logic (24 hours)
    const [isExpired, setIsExpired] = useState(false);
    useEffect(() => {
        if (!data?.createdAt) return;
        const checkExpiration = () => {
            const now = new Date();
            const createdAt = new Date(data.createdAt);
            const diffInHours = (now - createdAt) / (1000 * 60 * 60);
            setIsExpired(diffInHours >= 24);
        };
        checkExpiration();
        const interval = setInterval(checkExpiration, 60000); // Check every minute
        return () => clearInterval(interval);
    }, [data?.createdAt]);

    const handleStatusUpdate = async (newStatus, additionalData = {}) => {
        try {
            if (newStatus === "cancelled") {
                setIsRejecting(true);
            } else {
                setIsAccepting(true);
            }
            const res = await orderService.updateOrderStatus(orderId, newStatus, additionalData);
            if (onStatusChange) {
                // Pass the fully updated order from the API to instantly populate the UI correctly
                onStatusChange(newStatus, additionalData, res.order);
            }
            return res; // Return the response so handleAcceptOrder can use the latest data
        } catch (err) {
            console.error("Failed to update status", err);
            throw err; // Propagate error so auto-print doesn't proceed
        } finally {
            setTimeout(() => {
                setIsAccepting(false);
                setIsRejecting(false);
            }, 5000);
        }
    };

    const handleDirectPrint = async () => {
        if (isPrinting) return;
        setIsPrinting(true);
        try {
            Toast({
                message: "Printing receipt...",
                type: "info",
            });
            await thermalPrinterService.printReceipt(data, rawOrderData, storeName || "Restaurant");
            Toast({
                message: "Receipt printed successfully!",
                type: "success",
            });
        } catch (error) {
            console.error("Direct print failed:", error);
            Toast({
                message: `Print failed: ${error.message || "Unknown error"}`,
                type: "error",
            });
        } finally {
            setIsPrinting(false);
        }
    };

    const handleAcceptOrder = async () => {
        const savedAutoPrint = localStorage.getItem("foodie_auto_print_on_accept");
        // Default to true if never set or explicitly enabled, only false if set to "false"
        const autoPrint = savedAutoPrint !== "false";

        try {
            if (autoPrint) {
                Toast({
                    message: "Accepting order & generating receipt...",
                    type: "info",
                });
            }

            // Await the API call. Because of the backend update, this returns the FULLY updated order
            // (including the newly generated pickupCode from Roundtrip).
            const updatedOrderRes = await handleStatusUpdate("preparing", {estimatedMinutes: prepTime});

            if (autoPrint && updatedOrderRes?.order) {
                console.log("OrderCard - Received deterministic API response with pickup code, printing receipt...");
                const formattedLatest = formatApiOrder(updatedOrderRes.order);
                await thermalPrinterService.printReceipt(
                    formattedLatest,
                    updatedOrderRes.order,
                    storeName || "Restaurant"
                );
                Toast({message: "Auto-print successful!", type: "success"});
            }
        } catch (error) {
            console.error("Accept & Print failed:", error);
            if (autoPrint) {
                Toast({message: `Auto-print failed: ${error.message || "Unknown error"}`, type: "error"});
            }
        }
    };

    // Timer Logic for Preparing State (Countdown)
    // Also runs for "accepted" if food is NOT marked ready yet
    const [timeLeft, setTimeLeft] = useState(null);
    const [isOverdue, setIsOverdue] = useState(false);

    const isFoodNotReady = !data?.preparation?.readyAt;

    useEffect(() => {
        if (data?.status === "preparing" || (data?.status === "accepted" && isFoodNotReady)) {
            const calculateTimeLeft = () => {
                const targetTime = data?.preparation?.estimatedReadyAt
                    ? new Date(data.preparation.estimatedReadyAt).getTime()
                    : null;
                if (!targetTime) return;

                const now = new Date().getTime();
                const diff = targetTime - now;

                if (diff < 0) {
                    setIsOverdue(true);
                    setTimeLeft(Math.abs(diff));
                } else {
                    setIsOverdue(false);
                    setTimeLeft(diff);
                }
            };

            calculateTimeLeft();
            const timer = setInterval(calculateTimeLeft, 1000);
            return () => clearInterval(timer);
        }
    }, [data?.status, data?.preparation?.estimatedReadyAt, isFoodNotReady]);

    const formatTime = ms => {
        if (ms == null) return "--:--";
        const totalSeconds = Math.floor(ms / 1000);
        const totalMinutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours > 0) {
            return minutes > 0 ? `${hours}hr ${minutes}m` : `${hours}hr`;
        }
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    // Prep elapsed time (static) - shows when food is marked ready
    const [prepElapsed, setPrepElapsed] = useState(null);

    useEffect(() => {
        if (["ready_for_pickup", "ready"].includes(data?.status) || (data?.status === "accepted" && !isFoodNotReady)) {
            const setAt = data?.preparation?.setAt;
            const readyAt = data?.preparation?.readyAt;

            if (!setAt || !readyAt) {
                setPrepElapsed(null);
                return;
            }

            const startTime = new Date(setAt).getTime();
            const endTime = new Date(readyAt).getTime();
            const diff = endTime - startTime;
            setPrepElapsed(diff > 0 ? diff : 0);
        } else {
            setPrepElapsed(null);
        }
    }, [data?.status, data?.preparation?.setAt, data?.preparation?.readyAt, isFoodNotReady]);

    // Delivery elapsed timer - live for out_for_delivery, static for delivered
    const [deliveryElapsed, setDeliveryElapsed] = useState(null);

    useEffect(() => {
        if (["out_for_delivery", "ownership_taken", "ownership taken"].includes(data?.status)) {
            const startedAt = data?.delivery?.startedAt;
            if (!startedAt) {
                setDeliveryElapsed(null);
                return;
            }

            const calculateDeliveryTime = () => {
                const start = new Date(startedAt).getTime();
                const now = new Date().getTime();
                setDeliveryElapsed(now - start);
            };

            calculateDeliveryTime();
            const timer = setInterval(calculateDeliveryTime, 1000);
            return () => clearInterval(timer);
        } else if (["completed", "delivered"].includes(data?.status)) {
            const startedAt = data?.delivery?.startedAt;
            const deliveredAt = data?.delivery?.deliveredAt;

            if (startedAt && deliveredAt) {
                const start = new Date(startedAt).getTime();
                const end = new Date(deliveredAt).getTime();
                setDeliveryElapsed(end - start > 0 ? end - start : 0);
            } else {
                setDeliveryElapsed(null);
            }
        } else {
            setDeliveryElapsed(null);
        }
    }, [data?.status, data?.delivery?.startedAt, data?.delivery?.deliveredAt]);

    const orderStatus = e => {
        switch (e) {
            case "preparing":
                return <span className="status preparing">Preparing</span>;
            case "ready":
                return <span className="status ready">Ready</span>;
            case "ready_for_pickup":
                return <span className="status ready">Ready for Pickup</span>;
            case "accepted":
                return <span className="status accepted">Driver Accepted</span>;
            case "out_for_delivery":
                return <span className="status out_for_delivery">Out for Delivery</span>;
            case "ownership_taken":
                return <span className="status out_for_delivery">Out for Delivery</span>;
            case "ownership taken":
                return <span className="status out_for_delivery">Out for Delivery</span>;
            case "completed":
                return <span className="status completed">Delivered</span>;
            case "delivered":
                return <span className="status completed">Delivered</span>;
            case "new_order":
                return <span className="status pending">New Order</span>;
            case "cancelled":
                return <span className="status cancelled">Rejected</span>;
            case "expired":
                return <span className="status cancelled">Order Expired</span>;
            default:
                return <span className="status pending">Pending</span>;
        }
    };
    const orderStatusName = e => {
        switch (e) {
            case "preparing":
                return "preparing";
            case "ready":
                return "ready";
            case "ready_for_pickup":
                return "ready";
            case "accepted":
                return "accepted";
            case "out_for_delivery":
                return "out_for_delivery";
            case "ownership_taken":
                return "out_for_delivery";
            case "ownership taken":
                return "out_for_delivery";
            case "completed":
                return "completed";
            case "delivered":
                return "completed";
            case "new_order":
                return "pending";
            case "cancelled":
                return "cancelled";
            default:
                return "pending";
        }
    };
    const orderFrom = e => {
        // If no source or it's the default "foodie", show FlowDish branding
        if (!e || e.toLowerCase() === "foodie") {
            return <span className="foodie">FlowDish</span>;
        }
        const formatOrderSource = slug => {
            return (
                slug
                    // Insert space before capital letters (for camelCase)
                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                    // Insert space before common word boundaries in lowercase slugs
                    .replace(
                        /([a-z])(pizza|diner|cafe|restaurant|kitchen|grill|bistro|bar|pub|house|place|shop|bakery|eatery)/gi,
                        "$1 $2"
                    )
                    // Capitalize first letter of each word
                    .replace(/\b\w/g, char => char.toUpperCase())
            );
        };

        return <span className="external-order">{formatOrderSource(e)}</span>;
    };
    return (
        <div>
            <OrderDetails
                data={data}
                rawOrderData={rawOrderData}
                closeSidebar={() => setDetailsSidebar(false)}
                detailsSidebar={detailsSidebar}
                storeName={storeName}
                onCancelOrder={() => handleStatusUpdate("cancelled")}
                isRejecting={isRejecting}
                onRedispatchSuccess={(updatedOrder) => {
                    if (onStatusChange) {
                        onStatusChange(data.status, {}, updatedOrder);
                    }
                }}
            />
            <StyledOrderCard $index={index}>
                <CardHeader className="card-header" $status={orderStatusName(data?.status)}>
                    <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                        {orderStatus(
                            isExpired && !["completed", "delivered", "cancelled"].includes(data?.status)
                                ? "expired"
                                : data?.status
                        )}
                        {data?.status !== "cancelled" && (
                            <button
                                className="header-print-btn"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleDirectPrint();
                                }}
                                title="Print Receipt"
                                style={{
                                    background: "rgba(255, 255, 255, 0.2)",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "4px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    color: "white",
                                    transition: "all 0.2s",
                                }}>
                                <LuPrinter size={16} />
                            </button>
                        )}
                    </div>
                    {(data?.status === "new_order" ||
                        data?.status === "completed" ||
                        data?.status === "delivered" ||
                        data?.status === "cancelled" ||
                        isExpired) && (
                        <div className="time-wrap">
                            <img src={clockIcon} alt="clock" style={{filter: "brightness(0) invert(1)"}} />
                            <span className="time">{timeAgo}</span>
                        </div>
                    )}

                    {/* Prep countdown: preparing OR accepted when food not ready yet */}
                    {!isExpired &&
                        (data?.status === "preparing" || (data?.status === "accepted" && isFoodNotReady)) && (
                            <div
                                className={`timer-badge ${isOverdue ? "overdue" : ""}`}
                                style={{
                                    marginLeft: "auto",
                                    background: isOverdue ? "#FFEBEB" : "#E8F4FF",
                                    color: isOverdue ? "#D32F2F" : "#007BFF",
                                    padding: "4px 8px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }}>
                                <img
                                    src={clockIcon}
                                    alt="clock"
                                    style={{
                                        width: "12px",
                                        height: "12px",
                                        filter: isOverdue
                                            ? "invert(23%) sepia(51%) saturate(7400%) hue-rotate(352deg) brightness(88%) contrast(92%)"
                                            : "invert(35%) sepia(89%) saturate(2971%) hue-rotate(199deg) brightness(101%) contrast(106%)",
                                    }}
                                />
                                {isOverdue ? `Overdue by ${formatTime(timeLeft)}` : `${formatTime(timeLeft)} Left`}
                            </div>
                        )}

                    {/* Prep elapsed: ready_for_pickup OR accepted when food IS ready */}
                    {(["ready_for_pickup", "ready"].includes(data?.status) ||
                        (data?.status === "accepted" && !isFoodNotReady)) && (
                        <div
                            className="timer-badge"
                            style={{
                                marginLeft: "auto",
                                background: data?.driverCancellation?.type === "post_pickup" ? "#FFF3E0" : "#E8F5E9",
                                color: data?.driverCancellation?.type === "post_pickup" ? "#E65100" : "#2E7D32",
                                padding: "4px 8px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                            }}>
                            {data?.driverCancellation?.type === "post_pickup" ? (
                                <>⚠️ Rider Returning</>
                            ) : (
                                <>
                                    <img
                                        src={clockIcon}
                                        alt="clock"
                                        style={{
                                            width: "12px",
                                            height: "12px",
                                            filter: "invert(36%) sepia(77%) saturate(1478%) hue-rotate(94deg) brightness(97%) contrast(102%)",
                                        }}
                                    />
                                    Prep: {formatTime(prepElapsed)}
                                </>
                            )}
                        </div>
                    )}

                    {/* Delivery elapsed: live timer for out_for_delivery */}
                    {["out_for_delivery", "ownership_taken", "ownership taken"].includes(data?.status) && (
                        <div
                            className="timer-badge"
                            style={{
                                marginLeft: "auto",
                                background: "#FFF3E0",
                                color: "#E65100",
                                padding: "4px 8px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                            }}>
                            <img
                                src={clockIcon}
                                alt="clock"
                                style={{
                                    width: "12px",
                                    height: "12px",
                                    filter: "invert(30%) sepia(95%) saturate(4000%) hue-rotate(15deg) brightness(95%) contrast(100%)",
                                }}
                            />
                            🚗 {deliveryElapsed != null ? formatTime(deliveryElapsed) : "--:--"}
                        </div>
                    )}

                    {/* Delivered: shows total delivery duration */}
                    {["completed", "delivered"].includes(data?.status) && deliveryElapsed != null && (
                        <div
                            className="timer-badge"
                            style={{
                                marginLeft: "auto",
                                background: "#E8F5E9",
                                color: "#2E7D32",
                                padding: "4px 8px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                            }}>
                            <img
                                src={clockIcon}
                                alt="clock"
                                style={{
                                    width: "12px",
                                    height: "12px",
                                    filter: "invert(36%) sepia(77%) saturate(1478%) hue-rotate(94deg) brightness(97%) contrast(102%)",
                                }}
                            />
                            Delivered in {formatTime(deliveryElapsed)}
                        </div>
                    )}
                </CardHeader>
                <div className="card-body">
                    <div className="card-head">
                        <div className="count-wrap">
                            <strong className="count">
                                <span className="tooltip">
                                    {data?.bookingCode && data?.bookingCode !== "N/A" ? (
                                        <>
                                            <div className="booking-code-wrap">
                                                <span className="booking-code-value">{data?.bookingCode}</span>
                                                <span
                                                    className="dot"
                                                    style={{
                                                        backgroundColor:
                                                            data?.orderFrom === "Foodie"
                                                                ? "rgba(44, 185, 255, 1)"
                                                                : "rgba(133, 39, 196, 1)",
                                                    }}></span>
                                            </div>
                                            {data?.id && <span className="tracking-id">#{data?.id}</span>}
                                        </>
                                    ) : (
                                        <div className="order-id-wrap">
                                            <span className="order-id">#{data?.id}</span>
                                            <span
                                                className="dot"
                                                style={{
                                                    backgroundColor:
                                                        data?.orderFrom === "Foodie"
                                                            ? "rgba(44, 185, 255, 1)"
                                                            : "rgba(133, 39, 196, 1)",
                                                }}></span>
                                        </div>
                                    )}
                                </span>
                            </strong>
                        </div>
                        {data?.pickupCode &&
                            data?.pickupCode !== "N/A" &&
                            [
                                "new_order",
                                "preparing",
                                "accepted",
                                "out_for_delivery",
                                "ownership_taken",
                                "ownership taken",
                                "completed",
                                "delivered",
                                "ready_for_pickup",
                            ].includes(data?.status) && (
                                <div className="pickup-code-section">
                                    <span className="pickup-label">Pickup Code</span>
                                    <div className="pickup-value-wrap">
                                        <strong className="pickup-value">{data.pickupCode}</strong>
                                        <img
                                            src={copyIcon}
                                            alt="copy"
                                            className="copy-icon"
                                            onClick={() => {
                                                navigator.clipboard
                                                    .writeText(data.pickupCode)
                                                    .then(() => {
                                                        Toast({
                                                            message: "Pickup code copied!",
                                                            type: "success",
                                                        });
                                                    })
                                                    .catch(err => console.error("Failed to copy:", err));
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                    </div>
                    <div className="details-wrapper">
                        <ul className="details-list">
                            <li>
                                <img src={personIcon} alt="icon" />
                                <strong>{data?.name}</strong>
                            </li>
                            <li>
                                <img src={phoneIcon} alt="icon" />
                                <span>{data?.phone}</span>
                            </li>
                            {data?.orderType === "Delivery" && (
                                <li>
                                    <img src={locationIcon} alt="icon" />
                                    <span>{data?.address}</span>
                                </li>
                            )}
                            <li>
                                <img src={itemsIcon} alt="icon" />
                                <strong>Items ({data?.items?.length})</strong>
                            </li>
                        </ul>
                        {data?.status === "new_order" && (
                            <div className="prep-time-stepper">
                                <span className="label">Adjust Prep Time</span>
                                <div className="stepper-controls">
                                    <button onClick={() => setPrepTime(Math.max(5, prepTime - 5))}>
                                        <LuMinus size={12} />5
                                    </button>
                                    <span className="value">{prepTime}m</span>
                                    <button onClick={() => setPrepTime(prepTime + 5)}>
                                        <LuPlus size={12} />5
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <ul className="items-list">
                        {data?.items?.map((item, index) => (
                            <li key={index}>
                                <span className="item">
                                    {item?.title} × {item?.quantity}
                                </span>
                                <strong className="add-on">{item?.addOn}</strong>
                            </li>
                        ))}
                    </ul>

                    <div className="price-wrap">
                        <div className="order-type-wrap">
                            {data?.orderType === "Pick-up" || rawOrderData?.orderType === "pickup" ? (
                                <span className="order-type">PickUp Order</span>
                            ) : (
                                <>
                                    {!(
                                        rawOrderData?.payment?.status === "cod_pending" ||
                                        rawOrderData?.paymentStatus === "cod_pending"
                                    ) && <span className="order-type">{data?.orderType}</span>}
                                    {(rawOrderData?.payment?.status === "cod_pending" ||
                                        rawOrderData?.paymentStatus === "cod_pending") && (
                                        <span className="order-type">Cash on Delivery</span>
                                    )}
                                </>
                            )}
                            {orderFrom(data?.orderFrom)}
                        </div>
                        <strong className="price">${data?.price}</strong>
                    </div>
                    <div className="action-wrapper">
                        <div className="btn-wrap">
                            {data?.status === "new_order" && (
                                <>
                                    <Button onClick={handleAcceptOrder} disabled={isAccepting || isExpired}>
                                        {isAccepting ? (
                                            <>
                                                <span className="loading-spinner" />
                                                Accepting...
                                            </>
                                        ) : (
                                            <>
                                                <img src={tickIcon} alt="icon" />
                                                Accept
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        onClick={() => handleStatusUpdate("cancelled")}
                                        variant="cancel"
                                        disabled={isAccepting || isRejecting || isExpired}>
                                        {isRejecting ? (
                                            <>
                                                <span className="loading-spinner" />
                                                Rejecting...
                                            </>
                                        ) : (
                                            <>
                                                <img src={crossIcon} alt="icon" />
                                                Reject
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}

                            {(data?.status === "preparing" || data?.status === "accepted") && (
                                <>
                                    <Button
                                        onClick={() => handleStatusUpdate("ready_for_pickup")}
                                        variant={data?.status === "preparing" ? "preparing" : "accepted"}
                                        disabled={isExpired}>
                                        <img src={readyIcon} alt="icon" />
                                        Mark as Ready
                                    </Button>
                                    <button className="view-details-link" onClick={() => setDetailsSidebar(true)}>
                                        <img src={eyeIcon} alt="icon" />
                                        View Details
                                    </button>
                                </>
                            )}

                            {data?.status === "ready_for_pickup" && !data?.deliveryInfo && (
                                <>
                                    <Button width="182px" variant="ready" disabled>
                                        <img src={tickMark} alt="icon" />
                                        Waiting for Rider
                                    </Button>
                                    <button className="view-details-link" onClick={() => setDetailsSidebar(true)}>
                                        <img src={eyeIcon} alt="icon" />
                                        View Details
                                    </button>
                                </>
                            )}

                            {(data?.status === "out_for_delivery" || data?.status === "ownership_taken") &&
                                data?.deliveryInfo && (
                                    <>
                                        <div className="delivery-info">
                                            <p
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}>
                                                <strong>Rider:</strong> {data.deliveryInfo.driverName}
                                            </p>
                                            <Button variant="out_for_delivery" disabled>
                                                <img src={tickMark} alt="icon" />
                                                Out for Delivery
                                            </Button>
                                        </div>
                                        <button className="view-details-link" onClick={() => setDetailsSidebar(true)}>
                                            <img src={eyeIcon} alt="icon" />
                                            View Details
                                        </button>
                                    </>
                                )}

                            {(data?.status === "out_for_delivery" || data?.status === "ownership_taken") &&
                                !data?.deliveryInfo && (
                                    <>
                                        <Button variant="out_for_delivery" disabled>
                                            <img src={tickMark} alt="icon" />
                                            Out for Delivery
                                        </Button>
                                        <button className="view-details-link" onClick={() => setDetailsSidebar(true)}>
                                            <img src={eyeIcon} alt="icon" />
                                            View Details
                                        </button>
                                    </>
                                )}

                            {(data?.status === "completed" || data?.status === "delivered") && (
                                <>
                                    <Button variant="completed" disabled>
                                        <img src={tickMark} alt="icon" />
                                        Delivered
                                    </Button>
                                    <Button gap="10px" variant="completed" onClick={() => setDetailsSidebar(true)}>
                                        <img src={eyeIcon} alt="icon" />
                                        View Details
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </StyledOrderCard>
        </div>
    );
};

export default OrderCard;
